/**
 * Operating the build optimizer: the settings that meter it, what it has cost per day, and
 * every build with its token usage so an admin can see where the money went.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  BUILD_STATUSES,
  type AdminBuildStatsResponse,
  type AdminBuildsResponse,
  type BuildSettings,
  type BuildStatus,
  type BuildView,
} from '@thottbot/shared';
import { apiGet, apiSend } from '../../api/client';
import { relativeTime } from '../../util/time';
import { ConfirmButton } from '../ModQueue';
import { describeError } from '../Submit';

const SETTINGS_KEY = ['admin', 'builds', 'settings'];

function usd(n: number | null | undefined): string {
  return n === null || n === undefined ? '-' : `$${n.toFixed(4)}`;
}

export function Builds() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<BuildStatus | ''>('');
  const [form, setForm] = useState<BuildSettings | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => apiGet<BuildSettings>('/api/admin/builds/settings') });
  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);

  const stats = useQuery({ queryKey: ['admin', 'builds', 'stats'], queryFn: () => apiGet<AdminBuildStatsResponse>('/api/admin/builds/stats') });

  const builds = useQuery({
    queryKey: ['admin', 'builds', page, status],
    queryFn: () => apiGet<AdminBuildsResponse>(`/api/admin/builds?page=${page}${status ? `&status=${status}` : ''}`),
    // A queued or generating build changes on its own; follow it like the uploads page does.
    refetchInterval: (q) => (q.state.data?.items.some((b) => b.status === 'queued' || b.status === 'generating') ? 2000 : false),
    refetchIntervalInBackground: true,
  });

  const save = useMutation({
    mutationFn: (next: BuildSettings) => apiSend<BuildSettings>('PUT', '/api/admin/builds/settings', next),
    onSuccess: (data) => {
      setError(null);
      setNotice(data.enabled ? 'Build settings saved' : 'Builds are switched off');
      qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      qc.invalidateQueries({ queryKey: ['builds', 'options'] });
    },
    onError: (err) => {
      setNotice(null);
      setError(describeError(err));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiSend('DELETE', `/api/admin/builds/${id}`),
    onSuccess: () => {
      setError(null);
      setNotice('Build removed');
      qc.invalidateQueries({ queryKey: ['admin', 'builds'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  const total = builds.data?.total ?? 0;
  const pageSize = builds.data?.pageSize ?? 50;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <section className="section">
        <h2>Settings</h2>
        {notice && <p className="notice">{notice}</p>}
        {error && <p className="notice error">{error}</p>}
        {form && (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(form);
            }}
          >
            <label>
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled
            </label>
            <div className="field">
              <label htmlFor="daily-allowance">Daily allowance</label>
              <input
                id="daily-allowance"
                type="number"
                min={0}
                max={100}
                value={form.dailyAllowance}
                onChange={(e) => setForm({ ...form, dailyAllowance: Number(e.target.value) })}
              />
            </div>
            <div className="field">
              <label htmlFor="input-price">Input price (USD per million tokens)</label>
              <input
                id="input-price"
                type="number"
                min={0}
                step="0.01"
                value={form.inputUsdPerMTok}
                onChange={(e) => setForm({ ...form, inputUsdPerMTok: Number(e.target.value) })}
              />
            </div>
            <div className="field">
              <label htmlFor="output-price">Output price (USD per million tokens)</label>
              <input
                id="output-price"
                type="number"
                min={0}
                step="0.01"
                value={form.outputUsdPerMTok}
                onChange={(e) => setForm({ ...form, outputUsdPerMTok: Number(e.target.value) })}
              />
            </div>
            <p className="muted">
              Prices are what the cost column multiplies by. Bedrock bills separately from these defaults, which are the first-party
              Claude rates.
            </p>
            <button type="submit" disabled={save.isPending}>
              Save
            </button>
          </form>
        )}
      </section>

      <section className="section">
        <h2>Builds per day</h2>
        {stats.data && (
          <div className="table-scroll">
            <table className="grid" data-testid="admin-build-stats">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Builds</th>
                  <th>Failed</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {stats.data.days.map((d) => (
                  <tr key={d.day}>
                    <td>{d.day}</td>
                    <td>{d.builds}</td>
                    <td>{d.failed}</td>
                    <td>{usd(d.costUsd)}</td>
                  </tr>
                ))}
                <tr>
                  <th scope="row">Total</th>
                  <td>{stats.data.totals.builds}</td>
                  <td>{stats.data.totals.failed}</td>
                  <td>{usd(stats.data.totals.costUsd)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="section">
        <h2>Recent builds</h2>
        <div className="field">
          <label htmlFor="build-status-filter">Show</label>
          <select
            id="build-status-filter"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as BuildStatus | '');
              setPage(1);
            }}
          >
            <option value="">All</option>
            {BUILD_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {builds.data && (
          <>
            <div className="table-scroll">
              <table className="grid">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>Character</th>
                    <th>Status</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {builds.data.items.map((b: BuildView) => (
                    <tr key={b.id} data-testid="admin-build-row" data-status={b.status}>
                      <td>{relativeTime(b.createdAt)}</td>
                      <td>{b.ownerBattletag}</td>
                      <td>
                        <Link to={`/build/${b.id}`}>
                          {b.character.characterName ?? `Level ${b.character.level} ${b.character.race} ${b.character.class}`}
                        </Link>{' '}
                        <span className="muted">{b.character.role}</span>
                      </td>
                      <td>
                        {b.removed ? <span className="muted">removed</span> : b.status}
                        {b.error && <div className="muted">{b.error}</div>}
                      </td>
                      <td>
                        {b.usage ? `${b.usage.inputTokens} / ${b.usage.outputTokens}` : '-'}
                        {b.usage && b.usage.cacheReadTokens > 0 && <div className="muted">cache {b.usage.cacheReadTokens}</div>}
                      </td>
                      <td>{usd(b.usage?.costUsd)}</td>
                      <td>{!b.removed && <ConfirmButton label="Delete" onConfirm={() => remove.mutate(b.id)} />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pages > 1 && (
              <p className="pager">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </button>{' '}
                Page {page} of {pages}{' '}
                <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                  Next
                </button>
              </p>
            )}
          </>
        )}
      </section>
    </>
  );
}
